import React, { useState, useRef } from 'react';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import { Upload, X, Image as ImageIcon, Link as LinkIcon, Trash2, Edit2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const ImageUpload = ({ value, onChange, label = "Image", showPreview = true, onDelete }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadMethod, setUploadMethod] = useState('upload');
  const [urlInput, setUrlInput] = useState('');
  const [editingUrl, setEditingUrl] = useState(false);
  const [editUrlValue, setEditUrlValue] = useState('');
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Maximum file size is 5MB',
        variant: 'destructive',
      });
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload an image file',
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/admin/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      onChange(response.data.url);
      toast({
        title: 'Success',
        description: 'Image uploaded to S3 successfully',
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: error.response?.data?.detail || 'Failed to upload image',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUrlSubmit = () => {
    if (!urlInput.trim()) {
      toast({
        title: 'Invalid URL',
        description: 'Please enter a valid image URL',
        variant: 'destructive',
      });
      return;
    }

    onChange(urlInput.trim());
    setUrlInput('');
    toast({
      title: 'Success',
      description: 'Image URL added',
    });
  };

  const handleRemove = async () => {
    // If there's an onDelete callback and this is an S3 URL, try to delete from S3
    if (value && value.includes('s3') && value.includes('amazonaws.com')) {
      try {
        await api.delete('/admin/upload', { data: { url: value } });
        toast({
          title: 'Success',
          description: 'Image deleted from S3',
        });
      } catch (error) {
        console.error('Delete error:', error);
        // Continue with removal even if S3 delete fails
      }
    }
    
    onChange('');
    setUrlInput('');
    setEditingUrl(false);
    if (onDelete) onDelete();
  };

  const handleEditUrl = () => {
    setEditUrlValue(value);
    setEditingUrl(true);
  };

  const handleSaveEditUrl = () => {
    if (!editUrlValue.trim()) {
      toast({
        title: 'Invalid URL',
        description: 'Please enter a valid image URL',
        variant: 'destructive',
      });
      return;
    }
    onChange(editUrlValue.trim());
    setEditingUrl(false);
    toast({
      title: 'Success',
      description: 'Image URL updated',
    });
  };

  const handleCancelEdit = () => {
    setEditingUrl(false);
    setEditUrlValue('');
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>

      {value && showPreview && (
        <div className="space-y-3">
          {/* Image Preview */}
          <div className="relative inline-block">
            <img
              src={value}
              alt="Preview"
              className="w-full max-w-xs h-32 object-cover rounded-lg border border-gray-200"
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found';
              }}
            />
            <button
              onClick={handleRemove}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
              type="button"
              title="Delete image"
            >
              <X size={16} />
            </button>
          </div>
          
          {/* URL Display and Edit */}
          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-500">Image URL</label>
            {editingUrl ? (
              <div className="flex gap-2">
                <Input
                  type="url"
                  value={editUrlValue}
                  onChange={(e) => setEditUrlValue(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="flex-1 text-sm"
                />
                <Button type="button" onClick={handleSaveEditUrl} size="sm">
                  Save
                </Button>
                <Button type="button" onClick={handleCancelEdit} variant="ghost" size="sm">
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex gap-2 items-center">
                <Input
                  value={value}
                  readOnly
                  className="flex-1 text-sm bg-gray-50"
                />
                <Button
                  type="button"
                  onClick={handleEditUrl}
                  variant="outline"
                  size="sm"
                  title="Edit URL"
                >
                  <Edit2 size={14} />
                </Button>
              </div>
            )}
            <p className="text-xs text-gray-500">
              Edit the URL to replace with any image from the web
            </p>
          </div>
        </div>
      )}

      {!value && (
        <Tabs value={uploadMethod} onValueChange={setUploadMethod} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="upload">
              <Upload size={16} className="mr-2" />
              Upload
            </TabsTrigger>
            <TabsTrigger value="url">
              <LinkIcon size={16} className="mr-2" />
              URL
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="space-y-3">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                disabled={uploading}
              />
              
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                variant="outline"
                className="w-full"
              >
                <ImageIcon size={20} className="mr-2" />
                {uploading ? 'Uploading to S3...' : 'Choose Image'}
              </Button>
              
              <p className="text-xs text-gray-500 mt-2">
                PNG, JPG, GIF, WebP up to 5MB • Stored on AWS S3
              </p>
            </div>
          </TabsContent>

          <TabsContent value="url" className="space-y-3">
            <div className="flex gap-2">
              <Input
                type="url"
                placeholder="https://example.com/image.jpg"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleUrlSubmit();
                  }
                }}
              />
              <Button
                type="button"
                onClick={handleUrlSubmit}
                variant="outline"
              >
                Add
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              Paste a direct link to an image from anywhere on the web
            </p>
          </TabsContent>
        </Tabs>
      )}

      {value && !showPreview && (
        <div className="flex items-center gap-2">
          <Input value={value} readOnly className="flex-1" />
          <Button
            type="button"
            onClick={handleRemove}
            variant="ghost"
            size="sm"
            className="text-red-600"
          >
            <X size={16} />
          </Button>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
